import { useMobileMenu } from '@lib/context/mobile-menu-context'
import { bhEnv } from '@lib/env'
import { ProductCategory } from '@medusajs/medusa'
import BHRequireAccount from '@modules/account/components/require-account'
import BHSocials from '@modules/account/components/socials'
import { BHHoverable } from '@modules/bh/components'
import useCustomerId from '@modules/bh/hooks/useCustomerId'
import Hamburger from '@modules/common/components/hamburger'
import CartDropdown from '@modules/layout/components/cart-dropdown'
import MobileMenu from '@modules/mobile-menu/templates'
import DesktopSearchModal from '@modules/search/templates/desktop-search-modal'
import clsx from 'clsx'
import { useProductCategories } from 'medusa-react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import logoRectWhite from '/public/images/logo-rect-white.png'
import logoRect from '/public/images/logo-rect.png'
import { sortProductCategories } from '@lib/data/statics/helpers'

const Nav = () => {
  const { pathname } = useRouter()
  const [isHome, setIsHome] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  const { product_categories, isLoading } = useProductCategories()
  const customerId = useCustomerId()

  const loggedIn = customerId !== null

  //useEffect that detects if window is scrolled > 5px on the Y axis
  useEffect(() => {
    if (isHome) {
      const detectScrollY = () => {
        if (window.scrollY > 5) {
          setIsScrolled(true)
        } else {
          setIsScrolled(false)
        }
      }

      window.addEventListener('scroll', detectScrollY)

      return () => {
        window.removeEventListener('scroll', detectScrollY)
      }
    }
  }, [isHome])

  useEffect(() => {
    pathname === '/' ? setIsHome(true) : setIsHome(false)
  }, [pathname])

  const { toggle } = useMobileMenu()

  return (
    <div
      className={clsx(
        'h-48 xs:h-16 top-0 inset-x-0 z-50' /* {
        "!fixed": isHome,
      } */
      )}
    >
      <header
        className={clsx(
          'relative bg-bh-black h-16 mx-auto border-b transition-colors border-transparent duration-200'
        )}
      >
        <nav
          className={clsx(
            'text-gray-900 flex xs:px-1 sm:px-8 items-center justify-between w-full h-full text-small-regular transition-colors duration-200'
          )}
        >
          <div className="flex-1 basis-0 h-full text-bh-white flex items-center">
            <div className="block sm:hidden">
              <Hamburger setOpen={toggle} />
            </div>
            <div className="xs:hidden sm:flex items-center">
              <BHSocials className=" text-bh-white" size={20} />
              <a
                href={`tel:${bhEnv.companyInfo.phone}`}
                className="text-lg ml-4 text-bh-white whitespace-nowrap"
              >
                {bhEnv.companyInfo.phone}
              </a>
            </div>
          </div>
          <div className="sm:hidden mt-2 flex flex-col justify-center xs:pr-4">
            <Link href={`${bhEnv.bhMarketingHome}`} >
              <a className="text-xl-semi uppercase">
                <Image
                  src={logoRectWhite}
                  layout="fixed"
                  width={108}
                  height={108 * (logoRectWhite.height / logoRectWhite.width)}
                  loading="eager"
                  priority={true}
                  quality={90}
                  objectFit="contain"
                  objectPosition="center"
                  alt="innovation from experience"
                  draggable="false"
                />
              </a>
            </Link>
          </div>
          <div className="flex items-center gap-x-6 h-full flex-1 basis-0 justify-end">
            <div className="flex items-center gap-x-6 h-full">
              {process.env.FEATURE_SEARCH_ENABLED && <DesktopSearchModal />}
              <Link href="/account">
                <a className="uppercase text-base text-bh-white xs:hidden">
                  {loggedIn ? 'Account' : 'Sign In'}
                </a>
              </Link>
            </div>
            <BHRequireAccount>
              <CartDropdown />
            </BHRequireAccount>
          </div>
        </nav>
        <div className="w-full xs:hidden pt-2 bg-bh-white h-32 flex flex-col items-center justify-center">
          <div className="flex cursor-pointer">
            <Link href={`${bhEnv.bhMarketingHome}`} passHref>
              <a>
                <Image
                  src={logoRect}
                  layout="fixed"
                  width={224}
                  height={224 * (logoRect.height / logoRect.width)}
                  loading="eager"
                  priority={true}
                  quality={90}
                  objectFit="contain"
                  objectPosition="center"
                  alt="innovation from experience"
                  draggable="false"
                />
              </a>
            </Link>
          </div>
          <div className="flex flex-1 gap-8 justify-center items-center uppercase">
            <Link href={`${bhEnv.bhMarketingHome}`} className="text-md">
              Home
            </Link>
            <BHHoverable
              parent={
                <Link href="/store" className="text-md">
                  Products
                </Link>
              }
              child={
                isLoading ? undefined : (
                  <ProductCategoryList categories={product_categories ?? []} />
                )
              }
            />
            {/* <Link href="/gallery" className="text-md">
              Gallery
            </Link>
            <Link href="/about" className="text-md">
              About
            </Link> */}
            <BHRequireAccount>
              <Link href="/quotes" className="text-md">
                Quotes
              </Link>
            </BHRequireAccount>
          </div>
        </div>
        <MobileMenu />
      </header>
    </div>
  )
}

export default Nav

interface ProductCategoryListProps {
  categories: ProductCategory[]
}

function ProductCategoryList({ categories }: ProductCategoryListProps) {
  const sortedCategories = sortProductCategories(categories).filter(
    (c) => c.parent_category_id === null
  )

  // Move category with name "Hearth" to end of list
  const hearthIndex = categories.findIndex((cat) => cat.name === 'Hearth')
  if (hearthIndex !== -1) {
    const hearth = categories.splice(hearthIndex, 1)
    categories.push(hearth[0])
  }

  return (
    <div className="flex flex-col min-w-[128px] w-[280px] max-w-[540px] p-1">
      {sortedCategories.map((cat, i) => (
        <Link key={i} href={`/store?categoryId=${cat.id}`} className="text-md">
          <span
            className={`p-2 border-t text-sm border-gray-500 font-bold last:border-b cursor-pointer transition-colors tracking-wide hover:bg-slate-200 rounded-sm`}
          >
            {cat.name}
          </span>
        </Link>
      ))}
    </div>
  )
}
